import _extends from "@babel/runtime/helpers/extends";
import classNames from "classnames/dedupe";
import PropTypes from "prop-types";
import React from "react";
import { constants } from "../../constants";
import { Button } from "../button";
/**
 * @typedef {"xs" | "sm" | "md" | "lg" | "xl" | "2xl" | "3xl" | "4xl" | "5xl" | "6xl" | "full" | "max"} ModalSize
 */

/**
 *
 * @param {object} props
 * @param {string} [props.title]
 * @param {boolean} props.isOpen
 * @param {(e: MouseEvent) => (Promise<any>|void)} [props.onClose]
 * @param {ModalSize} props.size
 * @param {any} props.children
 * @param {any} props.className
 * @param {(props: { [key: string]: any }) => JSX.Element} [props.ModalOverlay]
 * @param {(props: { children: any, size: string, [key: string]: any }) => JSX.Element} [props.ModalContainer]
 * @param {(props: { children: any, size: string, [key: string]: any }) => JSX.Element} [props.ModalTitle]
 * @param {(props: { children: any, [key: string]: any }) => JSX.Element} [props.ModalContent]
 * @returns {JSX.Element}
 */

export const Modal = ({
  title,
  isOpen,
  onClose,
  size,
  children,
  className,
  ModalOverlay,
  ModalContainer,
  ModalTitle,
  ModalContent
}) => {
  const modalClass = classNames(["modal fixed w-full h-full top-0 left-0 flex items-center justify-center z-30"], {
    "opacity-0 pointer-events-none": !isOpen
  }, className);
  return /*#__PURE__*/React.createElement("div", {
    className: modalClass
  }, /*#__PURE__*/React.createElement(ModalOverlay, null), /*#__PURE__*/React.createElement(ModalContainer, {
    size: size
  }, title || onClose ? /*#__PURE__*/React.createElement(ModalTitle, {
    size: size
  }, /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("span", null, title), onClose ? /*#__PURE__*/React.createElement(Button, {
    onClick: onClose,
    size: "narrow",
    theme: "transparent",
    className: {
      "bg-en-yellow-700": false,
      "focus:outline-none": false,
      "px-2": true,
      "px-4": false
    }
  }, /*#__PURE__*/React.createElement("svg", {
    xmlns: "http://www.w3.org/2000/svg",
    width: "14.251",
    height: "14.251",
    viewBox: "0 0 14.251 14.251"
  }, /*#__PURE__*/React.createElement("rect", {
    width: "17.995",
    height: "2.159",
    rx: "1.08",
    transform: "translate(1.527) rotate(45)",
    fill: "currentColor"
  }), /*#__PURE__*/React.createElement("rect", {
    "data-name": "Rectangle 146",
    width: "17.995",
    height: "2.159",
    rx: "1.08",
    transform: "translate(14.252 1.527) rotate(135)",
    fill: "currentColor"
  }))) : null)) : null, /*#__PURE__*/React.createElement(ModalContent, null, children)));
};
Modal.propTypes = {
  title: PropTypes.string,
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func,
  size: PropTypes.oneOf(constants.RESPONSIVE_SIZES),
  children: PropTypes.element.isRequired,
  className: PropTypes.any,
  ModalOverlay: PropTypes.func,
  ModalContainer: PropTypes.func,
  ModalTitle: PropTypes.func,
  ModalContent: PropTypes.func
};
/**
 *
 * @param {{ className?: any, children?: any }} props
 * @returns {JSX.Element}
 */
// eslint-disable-next-line react/prop-types

export const ModalOverlay = ({
  className,
  ...props
}) => {
  const overlayClassName = classNames("modal-overlay absolute w-full h-full bg-gray-900 opacity-50", className);
  return /*#__PURE__*/React.createElement("div", _extends({
    className: overlayClassName
  }, props));
};
/**
 *
 * @param {{ className?: any, children?: any, size?: string, [key: string]: any }} props
 * @returns {JSX.Element}
 */
// eslint-disable-next-line react/prop-types

export const ModalContainer = ({
  className,
  children,
  size,
  ...props
}) => {
  const containerClassName = classNames(`modal-container bg-white w-11/12 mx-auto my-4 rounded shadow-lg z-50 max-h-full overflow-y-auto`, {
    [`md:max-w-${size || "md"}`]: size !== "max" // size "max" with className "max-w-max" does not work as expected, and is best ignored

  }, className);
  return /*#__PURE__*/React.createElement("div", _extends({}, props, {
    className: containerClassName
  }), children);
};
/**
 *
 * @param {{ className?: any, children?: any, size?: string, [key: string]: any }} props
 * @returns {JSX.Element}
 */
// eslint-disable-next-line react/prop-types

export const ModalTitle = ({
  className,
  children,
  size,
  ...props
}) => {
  const titleClassName = classNames([`bg-en-yellow-400 flex justify-between items-center pl-6 pr-2 font-bold`], {
    "py-2 text-lg": constants.SIZE_VALUES[size] < constants.SIZE_VALUES["lg"],
    "py-4 text-2xl": constants.SIZE_VALUES[size] >= constants.SIZE_VALUES["lg"]
  }, className);
  return /*#__PURE__*/React.createElement("div", _extends({
    className: titleClassName
  }, props), children);
};
/**
 *
 * @param {{ className?: any, children?: any, size?: string, [key: string]: any }} props
 * @returns {JSX.Element}
 */
// eslint-disable-next-line react/prop-types

export const ModalContent = ({
  className,
  children,
  ...props
}) => {
  const contentClassName = classNames("modal-content py-4 text-left px-6 bg-en-yellow-100", className);
  return /*#__PURE__*/React.createElement("div", _extends({
    className: contentClassName
  }, props), children);
};
Modal.defaultProps = {
  ModalOverlay,
  ModalTitle,
  ModalContainer,
  ModalContent
};
Modal.displayName = "Modal";